import { useState, useEffect, useCallback, useRef, ReactNode, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import './FullscreenChart.css';

// Context to communicate fullscreen state to child charts
const FullscreenContext = createContext(false);

export function useIsFullscreen(): boolean {
  return useContext(FullscreenContext);
}


interface FullscreenChartProps {
  children: ReactNode;
  title: string;
}

export function FullscreenChart({ children, title }: FullscreenChartProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const expandButtonRef = useRef<HTMLButtonElement>(null);

  const handleClose = useCallback(() => {
    setIsExpanded(false);
  }, []);

  // Handle Escape key
  useEffect(() => {
    if (!isExpanded) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isExpanded, handleClose]);

  // Lock body scroll when expanded
  useEffect(() => {
    if (isExpanded) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isExpanded]);

  // Focus management: move focus to close button when opened, return to expand button when closed
  useEffect(() => {
    if (isExpanded) {
      closeButtonRef.current?.focus();
    } else {
      expandButtonRef.current?.focus();
    }
  }, [isExpanded]);

  // Force ResponsiveContainer to remeasure after expanding
  useEffect(() => {
    if (isExpanded) {
      // Small delay to ensure DOM is ready, then trigger resize
      const timer = setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isExpanded]);

  // Render children inside Provider with appropriate value for each render location
  return (
    <>
      <div className="FullscreenChart-wrapper">
        <button
          ref={expandButtonRef}
          className="FullscreenChart-expandButton"
          onClick={() => setIsExpanded(true)}
          aria-label="View fullscreen"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3" />
          </svg>
        </button>
        {!isExpanded && (
          <FullscreenContext.Provider value={false}>
            {children}
          </FullscreenContext.Provider>
        )}
      </div>
      {isExpanded && createPortal(
        <FullscreenContext.Provider value={true}>
          <div
            className="FullscreenChart-backdrop"
            onClick={handleClose}
            role="dialog"
            aria-modal="true"
            aria-label={`${title} fullscreen view`}
          >
            <div
              className="FullscreenChart-container"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                ref={closeButtonRef}
                className="FullscreenChart-closeButton"
                onClick={handleClose}
                aria-label="Close fullscreen"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
              <h3 className="FullscreenChart-title">{title}</h3>
              <div className="FullscreenChart-content">
                {children}
              </div>
            </div>
          </div>
        </FullscreenContext.Provider>,
        document.body
      )}
    </>
  );
}
